import { useState, useEffect, useRef } from 'react';
import { useResourceRating, Resource as ResourceFromResources } from '../../hooks/api/useResources';
import { Resource as ResourceFromExperiences } from '../../hooks/api/useExperiences';
import { StarButtonBase } from './star-button-base';

// Union type that accepts either Resource type, with optional properties for compatibility
type Resource = (ResourceFromResources | ResourceFromExperiences) & {
  rating?: number;
};

interface ResourceStarButtonProps {
  resource: Resource;
  className?: string;
}

export const ResourceStarButton: React.FC<ResourceStarButtonProps> = ({ resource, className = '' }) => {
  const rateResourceMutation = useResourceRating();
  const [optimisticRating, setOptimisticRating] = useState<number | null>(null);
  const resourceIdRef = useRef(resource.id);

  // Reset optimistic state if resource ID changes
  useEffect(() => {
    if (resourceIdRef.current !== resource.id) {
      resourceIdRef.current = resource.id;
      setOptimisticRating(null);
    }
  }, [resource.id]);

  // Use optimistic rating if available, otherwise use resource rating
  const currentRating = optimisticRating !== null ? optimisticRating : resource.rating || 0;
  const isFavorite = currentRating > 0;
  const isLoading = rateResourceMutation.isLoading;

  const handleToggle = () => {
    const newRating = isFavorite ? 0 : 1;
    setOptimisticRating(newRating);

    rateResourceMutation.mutate(
      {
        resourceId: resource.id,
        rating: newRating,
      },
      {
        onSuccess: () => {
          if (resource.rating === newRating) {
            setOptimisticRating(null);
          }
        },
        onError: () => {
          setOptimisticRating(null);
        },
      },
    );
  };

  useEffect(() => {
    if (optimisticRating !== null && resource.rating === optimisticRating) {
      setOptimisticRating(null);
    }
  }, [resource.rating, optimisticRating]);

  return <StarButtonBase isFavorite={isFavorite} onToggle={handleToggle} isLoading={isLoading} className={className} />;
};
